import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage } from "@/App";
import { guideApi } from "@/lib/guide";
import { colorFor } from "@/lib/guideColors";
import * as Icons from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Search, BookOpen, Star, Loader2, ChevronLeft, Sparkles, X } from "lucide-react";

export default function GuideHome() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const initialCat = params.get("cat") || "";

  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(initialQ);
  const [activeCat, setActiveCat] = useState(initialCat);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    Promise.all([guideApi.categories(), guideApi.featured()])
      .then(([cats, feat]) => { setCategories(cats); setFeatured(feat); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setSearching(true);
    const params = {};
    if (activeCat) params.category = activeCat;
    if (q && q.length >= 2) params.q = q;
    guideApi.list(params).then(setArticles).finally(() => setSearching(false));
  }, [activeCat, q]);

  const updateUrl = (next) => {
    const p = new URLSearchParams();
    if (next.q) p.set("q", next.q);
    if (next.cat) p.set("cat", next.cat);
    setParams(p, { replace: true });
  };

  return (
    <DashboardLayout title={t("دليل المهاجر", "Immigrant Guide")}>
      <div className="space-y-6" data-testid="guide-home">
        {/* Hero + Search */}
        <section className="glass-card rounded-3xl p-6 lg:p-10 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-yellow-500/10 blur-[120px] rounded-full" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-blue-500/10 blur-[120px] rounded-full" />
          <div className="relative space-y-4 max-w-3xl">
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              <Sparkles size={12} className="ml-1" />
              {t("مركز معلومات وإرشادات", "Information & Guidance Center")}
            </Badge>
            <h1 className="text-3xl lg:text-5xl font-bold text-luxury">{t("🇺🇸 دليل المهاجر العربي في أمريكا", "Arab Immigrant Guide to America")}</h1>
            <p className="text-slate-300 text-base lg:text-lg">{t(
              "نصائح عملية مختصرة لكل ما تحتاجه: السكن، الهجرة، الضرائب، العمل، الصحة — بالعربية وبخطوات واضحة.",
              "Concise, practical guidance on everything you need — housing, immigration, taxes, jobs, health — in Arabic with clear steps."
            )}</p>
            <div className="relative">
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-yellow-500" size={20} />
              <input
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); updateUrl({ q: e.target.value, cat: activeCat }); }}
                placeholder={t("ابحث في الدليل... (مثلاً: SSN، إيجار، Green Card)", "Search the guide... (e.g., SSN, rent, Green Card)")}
                className="w-full bg-slate-900/80 border-2 border-slate-700 focus:border-yellow-500 rounded-2xl pr-14 pl-12 py-4 text-white outline-none text-lg"
                data-testid="guide-search-input"
              />
              {q && (
                <button onClick={() => { setQ(""); updateUrl({ q: "", cat: activeCat }); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl lg:text-2xl font-bold text-white flex items-center gap-2">
              <BookOpen className="text-yellow-500" size={22} />
              {t("الفئات", "Categories")}
            </h2>
            {activeCat && (
              <button onClick={() => { setActiveCat(""); updateUrl({ q, cat: "" }); }} className="text-sm text-yellow-500 hover:underline" data-testid="clear-cat">
                {t("× مسح الفلتر", "× Clear filter")}
              </button>
            )}
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">{[...Array(14)].map((_, i) => <div key={i} className="glass-card rounded-2xl h-24 skeleton" />)}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {categories.map((c) => {
                const Icon = Icons[c.icon] || BookOpen;
                const isActive = c.key === activeCat;
                const cc = colorFor(c.color);
                return (
                  <button
                    key={c.key}
                    onClick={() => { const next = isActive ? "" : c.key; setActiveCat(next); updateUrl({ q, cat: next }); }}
                    className={`glass-card rounded-2xl p-3 text-center transition-all hover:scale-105 border ${isActive ? `ring-2 ${cc.ring} ${cc.bg} ${cc.border}` : "border-transparent"}`}
                    data-testid={`cat-${c.key}`}
                  >
                    <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${cc.bg}`}>
                      <Icon className={cc.icon} size={20} />
                    </div>
                    <p className={`text-xs font-semibold ${isActive ? cc.text : "text-white"}`}>{language === "ar" ? c.ar : c.en}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{c.count} {t("مقال", "articles")}</p>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Featured (only when no filter) */}
        {!q && !activeCat && (
          <section>
            <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Star className="text-yellow-500 fill-yellow-500" size={22} />
              {t("الأكثر أهمية", "Most Important")}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((a) => (
                <ArticleCard key={a.slug} a={a} categories={categories} language={language} t={t} onClick={() => navigate(`/guide/article/${a.slug}`)} />
              ))}
            </div>
          </section>
        )}

        {/* Results */}
        <section>
          <h2 className="text-lg lg:text-xl font-bold text-white mb-3">
            {q
              ? `${t("نتائج البحث عن:", "Results for:")} "${q}"`
              : activeCat
              ? categories.find((c) => c.key === activeCat)?.[language === "ar" ? "ar" : "en"]
              : t("جميع المقالات", "All articles")}
            <span className="text-sm text-slate-500 mr-2">({articles.length})</span>
          </h2>
          {searching ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-yellow-500" size={32} /></div>
          ) : articles.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <Search className="text-slate-600 mx-auto mb-3" size={40} />
              <p className="text-slate-300">{t("لم نجد نتائج. جرب كلمات أخرى.", "No results. Try different keywords.")}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((a) => (
                <ArticleCard key={a.slug} a={a} categories={categories} language={language} t={t} onClick={() => navigate(`/guide/article/${a.slug}`)} />
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

function ArticleCard({ a, categories, language, t, onClick }) {
  const cat = categories.find((c) => c.key === a.category);
  const Icon = (cat && Icons[cat.icon]) || BookOpen;
  const cc = colorFor(cat?.color);
  return (
    <button
      onClick={onClick}
      className="glass-card rounded-2xl p-5 text-right hover:scale-[1.02] transition-all relative overflow-hidden group"
      data-testid={`article-card-${a.slug}`}
    >
      <div className="absolute top-3 left-3 opacity-30 group-hover:opacity-100 transition">
        <ChevronLeft size={18} className={cc.icon} />
      </div>
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cc.bg}`}>
          <Icon className={cc.icon} size={18} />
        </div>
        <div className="flex-1 min-w-0">
          {cat && <Badge className={`bg-slate-800 text-[10px] mb-1 ${cc.text}`}>{language === "ar" ? cat.ar : cat.en}</Badge>}
          <h3 className="font-bold text-white text-base leading-tight line-clamp-2">{a.title}</h3>
        </div>
      </div>
      <p className="text-sm text-slate-400 line-clamp-3 leading-relaxed">{a.summary}</p>
      {a.featured && (
        <Badge className="absolute top-3 right-3 bg-yellow-500/20 text-yellow-400 text-[10px]">
          <Star size={9} className="ml-1 fill-current" />{t("مميز", "Featured")}
        </Badge>
      )}
    </button>
  );
}
